import { costOfRunning } from '../ultra/ultraPacing.js';

const STOP_SPEED_THRESHOLD_KMH = 1.0;
const STOP_DURATION_THRESHOLD_S = 30;

/**
 * Compute actual performance per section from timestamped trackpoints.
 * @param {Array} sections - Sections from groupSegmentsIntoSections (with segment trackpoint indices)
 * @param {Array} trackpoints - Trackpoints with .timestamp, .heartRate, etc.
 * @returns {Array|null} Actual performance array, or null if timestamps are missing
 */
export function extractActualPerformance(sections, trackpoints) {
  if (!trackpoints?.length || !trackpoints[0]?.timestamp) return null;

  const raceStart = trackpoints[0].timestamp;
  const costFlat = costOfRunning(0);
  const results = [];

  for (const section of sections) {
    const startIdx = section.segments[0].startTrackpointIndex;
    const endIdx = section.segments[section.segments.length - 1].endTrackpointIndex;

    const startTp = trackpoints[startIdx];
    const endTp = trackpoints[endIdx];

    if (!startTp?.timestamp || !endTp?.timestamp) {
      results.push(nullSectionResult(section));
      continue;
    }

    const elapsedMs = endTp.timestamp - startTp.timestamp;
    const elapsedMinutes = elapsedMs / 60000;
    const distanceKm = section.totalDistance / 1000;

    const actualPace = distanceKm > 0 ? elapsedMinutes / distanceKm : 0;
    const cumulativeMs = endTp.timestamp - raceStart;
    const cumulativeMinutes = cumulativeMs / 60000;

    const gapFactor = costOfRunning(section.averageGradient / 100) / costFlat;
    const gapDistanceKm = distanceKm * gapFactor;
    const actualGAP = gapDistanceKm > 0 ? elapsedMinutes / gapDistanceKm : 0;

    const { movingTimeMinutes, stoppedTimeMinutes } = computeMovingTime(trackpoints, startIdx, endIdx);

    const avgHR = computeAvgMetric(trackpoints, startIdx, endIdx, 'heartRate');
    const avgCadence = computeAvgMetric(trackpoints, startIdx, endIdx, 'cadence');
    const avgPower = computeAvgMetric(trackpoints, startIdx, endIdx, 'power');

    results.push({
      sectionNumber: section.sectionNumber,
      distanceKm,
      gradient: section.averageGradient,
      elapsedMinutes,
      movingTimeMinutes,
      stoppedTimeMinutes,
      actualPace,
      cumulativeMinutes,
      actualGAP,
      avgHeartRate: avgHR,
      avgCadence,
      avgPower
    });
  }

  return results;
}

function nullSectionResult(section) {
  return {
    sectionNumber: section.sectionNumber,
    distanceKm: section.totalDistance / 1000,
    gradient: section.averageGradient,
    elapsedMinutes: null,
    movingTimeMinutes: null,
    stoppedTimeMinutes: null,
    actualPace: null,
    cumulativeMinutes: null,
    actualGAP: null,
    avgHeartRate: null,
    avgCadence: null,
    avgPower: null
  };
}

function computeMovingTime(trackpoints, startIdx, endIdx) {
  let movingMs = 0;
  let stoppedMs = 0;

  for (let i = startIdx + 1; i <= endIdx; i++) {
    const prev = trackpoints[i - 1];
    const curr = trackpoints[i];
    if (!prev.timestamp || !curr.timestamp) continue;

    const dtMs = curr.timestamp - prev.timestamp;
    if (dtMs <= 0) continue;

    const dtHours = dtMs / 3600000;
    const distKm = (curr.cumulativeDistance - prev.cumulativeDistance) / 1000;
    const speedKmH = dtHours > 0 ? distKm / dtHours : 0;

    if (speedKmH < STOP_SPEED_THRESHOLD_KMH && dtMs > STOP_DURATION_THRESHOLD_S * 1000) {
      stoppedMs += dtMs;
    } else {
      movingMs += dtMs;
    }
  }

  return {
    movingTimeMinutes: movingMs / 60000,
    stoppedTimeMinutes: stoppedMs / 60000
  };
}

function computeAvgMetric(trackpoints, startIdx, endIdx, field) {
  let sum = 0;
  let count = 0;
  for (let i = startIdx; i <= endIdx; i++) {
    const val = trackpoints[i]?.[field];
    if (val != null && val > 0) {
      sum += val;
      count++;
    }
  }
  return count > 0 ? sum / count : null;
}

/**
 * Check whether trackpoints have timestamps (i.e. from a recorded activity, not a course file).
 */
export function hasTimestamps(trackpoints) {
  if (!trackpoints?.length) return false;
  let withTime = 0;
  const sampleSize = Math.min(trackpoints.length, 10);
  for (let i = 0; i < sampleSize; i++) {
    if (trackpoints[i]?.timestamp) withTime++;
  }
  return withTime >= sampleSize * 0.5;
}
